import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { bucketConfigService } from '../../services/bucketConfigService';
import NavigationBar from '../../components/ui/NavigationBar';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import BucketConfigCard from './components/BucketConfigCard';
import BucketConfigModal from './components/BucketConfigModal';
import AppPreferences from './components/AppPreferences';
import SessionManagement from './components/SessionManagement';
import PWAStatus from './components/PWAStatus';
import ConfigExportImport from './components/ConfigExportImport';
import StorageRequest from './components/StorageRequest';

const Settings = () => {
  const { user } = useAuth();
  const [activeTab, setActiveTab] = useState('buckets');
  const [isConfigModalOpen, setIsConfigModalOpen] = useState(false);
  const [editingBucket, setEditingBucket] = useState(null);
  const [buckets, setBuckets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const [preferences, setPreferences] = useState({
    maxFileSize: '10',
    allowedFileTypes: ['images', 'documents'],
    autoThumbnails: true,
    compressImages: true,
    enablePreviews: true,
    showMetadata: true,
    defaultGridView: true,
    itemsPerPage: '50',
    lazyLoading: true,
    cacheListings: true
  });

  const [session] = useState({
    user: {
      name: 'Sarah Mitchell',
      email: 'sarah.mitchell@company.com',
      avatar: "https://img.rocket.new/generatedImages/rocket_gen_img_1479bf0bf-1763297239550.png",
      avatarAlt: 'Professional headshot of woman with shoulder-length brown hair wearing navy blue blazer and white blouse'
    },
    loginTime: new Date(Date.now() - 7200000),
    tokenExpiry: new Date(Date.now() + 3600000),
    lastActivity: new Date(Date.now() - 120000)
  });

  // Load bucket configurations from Supabase
  useEffect(() => {
    if (!user) return;

    const loadBuckets = async () => {
      try {
        setLoading(true);
        setError('');
        const configs = await bucketConfigService?.getAll();
        
        // Convert to UI format
        const formattedBuckets = configs?.map(config => ({
          id: config?.id,
          name: config?.bucketName,
          bucketName: config?.bucketName,
          region: config?.bucketRegion,
          accessKey: config?.accessKeyId,
          secretKey: config?.secretAccessKey,
          publicUrl: config?.publicUrl,
          bucketType: config?.bucketType,
          isDefault: config?.isDefault,
          status: 'connected',
          lastTested: config?.lastAccessedAt ? 
            new Date(config.lastAccessedAt)?.toLocaleString('en-US', {
              month: 'short',
              day: 'numeric',
              hour: '2-digit',
              minute: '2-digit'
            }) : 'Not tested',
          errorMessage: null
        }));
        
        setBuckets(formattedBuckets);
      } catch (err) {
        console.error('Error loading buckets:', err);
        setError(err?.message || 'Failed to load bucket configurations');
      } finally {
        setLoading(false);
      }
    };

    loadBuckets();
  }, [user]);

  // Subscribe to real-time changes for bucket configurations
  useEffect(() => {
    if (!user) return;

    const channel = bucketConfigService?.subscribeToChanges(user?.id, (payload) => {
      if (payload?.eventType === 'INSERT') {
        // Add new bucket configuration
        const newBucket = {
          id: payload?.new?.id,
          name: payload?.new?.bucketName,
          bucketName: payload?.new?.bucketName,
          region: payload?.new?.bucketRegion,
          accessKey: payload?.new?.accessKeyId,
          secretKey: payload?.new?.secretAccessKey,
          publicUrl: payload?.new?.publicUrl,
          bucketType: payload?.new?.bucketType,
          isDefault: payload?.new?.isDefault,
          status: 'connected',
          lastTested: 'Just added',
          errorMessage: null
        };
        setBuckets(prev => [newBucket, ...prev]);
      } else if (payload?.eventType === 'UPDATE') {
        // Update existing bucket configuration
        setBuckets(prev => prev?.map(bucket => 
          bucket?.id === payload?.new?.id ? {
            ...bucket,
            name: payload?.new?.bucketName,
            bucketName: payload?.new?.bucketName,
            region: payload?.new?.bucketRegion,
            accessKey: payload?.new?.accessKeyId,
            secretKey: payload?.new?.secretAccessKey,
            publicUrl: payload?.new?.publicUrl,
            bucketType: payload?.new?.bucketType,
            isDefault: payload?.new?.isDefault
          } : bucket
        ));
      } else if (payload?.eventType === 'DELETE') {
        // Remove deleted bucket configuration
        setBuckets(prev => prev?.filter(bucket => bucket?.id !== payload?.old?.id));
      }
    });

    return () => {
      bucketConfigService?.unsubscribeFromChanges(channel);
    };
  }, [user]);

  const tabs = [
    { id: 'buckets', label: 'S3 Buckets', icon: 'Database' },
    { id: 'storage', label: 'Storage', icon: 'FolderPlus' },
    { id: 'preferences', label: 'Preferences', icon: 'Settings' },
    { id: 'session', label: 'Session', icon: 'User' },
    { id: 'pwa', label: 'PWA Status', icon: 'Smartphone' },
    { id: 'export', label: 'Export/Import', icon: 'Download' }
  ];

  const handleAddBucket = () => {
    setEditingBucket(null);
    setIsConfigModalOpen(true);
  };

  const handleEditBucket = (bucket) => {
    setEditingBucket(bucket);
    setIsConfigModalOpen(true);
  };

  const handleSaveBucket = async (bucketData) => {
    try {
      if (editingBucket) {
        await bucketConfigService?.update(bucketData?.id, {
          bucketName: bucketData?.bucketName,
          bucketRegion: bucketData?.region,
          accessKeyId: bucketData?.accessKey,
          secretAccessKey: bucketData?.secretKey,
          publicUrl: bucketData?.publicUrl,
          bucketType: bucketData?.bucketType,
          isDefault: bucketData?.isDefault
        });
      } else {
        await bucketConfigService?.create({
          bucketName: bucketData?.bucketName,
          bucketRegion: bucketData?.region,
          accessKeyId: bucketData?.accessKey,
          secretAccessKey: bucketData?.secretKey,
          publicUrl: bucketData?.publicUrl,
          bucketType: bucketData?.bucketType || 'personal',
          isDefault: bucketData?.isDefault || false
        });
      }
      setIsConfigModalOpen(false);
      setEditingBucket(null);
    } catch (err) {
      console.error('Error saving bucket:', err);
      setError(err?.message || 'Failed to save bucket configuration');
      throw err; // Re-throw to let modal handle the error display
    }
  };

  const handleRemoveBucket = async (bucketId) => {
    if (window.confirm('Are you sure you want to remove this bucket configuration?')) {
      try {
        await bucketConfigService?.delete(bucketId);
      } catch (err) {
        console.error('Error removing bucket:', err);
        setError(err?.message || 'Failed to remove bucket configuration');
      }
    }
  };

  const handleTestConnection = async (bucketId) => {
    setBuckets(buckets?.map((b) =>
      b?.id === bucketId ? { ...b, status: 'testing' } : b
    ));

    // Simulate connection test
    await new Promise((resolve) => setTimeout(resolve, 2000));

    setBuckets(buckets?.map((b) =>
      b?.id === bucketId ? {
        ...b,
        status: Math.random() > 0.3 ? 'connected' : 'error',
        lastTested: new Date()?.toLocaleString('en-US', {
          month: 'short',
          day: 'numeric',
          hour: '2-digit',
          minute: '2-digit'
        }),
        errorMessage: Math.random() > 0.3 ? null : 'Connection failed - Invalid credentials or network issue'
      } : b
    ));

    // Update last accessed timestamp
    try {
      await bucketConfigService?.updateLastAccessed(bucketId);
    } catch (err) {
      console.error('Error updating last accessed:', err);
    }
  };

  const handlePreferenceChange = (key, value) => {
    setPreferences((prev) => ({ ...prev, [key]: value }));
  };

  const handleRefreshToken = async () => {
    await new Promise((resolve) => setTimeout(resolve, 1500));
    console.log('Token refreshed successfully');
  };

  const handleSignOut = () => {
    if (window.confirm('Are you sure you want to sign out?')) {
      console.log('Signing out...');
      window.location.href = '/login';
    }
  };

  const handleImportConfig = (importedBuckets) => {
    const newBuckets = importedBuckets?.filter(
      (imported) => !buckets?.some((existing) => existing?.bucketName === imported?.bucketName)
    );
    setBuckets([...buckets, ...newBuckets]);
  };

  return (
    <div className="min-h-screen bg-background">
      <NavigationBar />
      <main className="main-content">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="mb-8">
            <div className="flex items-center gap-3 mb-2">
              <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center">
                <Icon name="Settings" size={20} color="var(--color-primary)" />
              </div>
              <h1 className="text-3xl font-bold text-foreground">Settings</h1>
            </div>
            <p className="text-muted-foreground">
              Manage your S3 bucket configurations, application preferences, and account settings
            </p>
          </div>

          <div className="flex flex-col lg:flex-row gap-6">
            <div className="lg:w-64 flex-shrink-0">
              <div className="bg-card border border-border rounded-lg p-2 sticky top-20">
                <nav className="space-y-1">
                  {tabs?.map((tab) => (
                    <button
                      key={tab?.id}
                      onClick={() => setActiveTab(tab?.id)}
                      className={`w-full flex items-center gap-3 px-4 py-3 rounded-md text-sm font-medium transition-colors duration-150 ${
                        activeTab === tab?.id
                          ? 'bg-primary text-primary-foreground'
                          : 'text-muted-foreground hover:bg-muted hover:text-foreground'
                      }`}
                    >
                      <Icon name={tab?.icon} size={18} />
                      <span>{tab?.label}</span>
                    </button>
                  ))}
                </nav>
              </div>
            </div>

            <div className="flex-1 min-w-0">
              {activeTab === 'buckets' && (
                <div>
                  <div className="flex items-center justify-between mb-6">
                    <div>
                      <h2 className="text-xl font-semibold text-foreground mb-1">S3 Bucket Configurations</h2>
                      <p className="text-sm text-muted-foreground">
                        Manage your S3 bucket connections and credentials
                      </p>
                    </div>
                    <Button
                      iconName="Plus"
                      iconPosition="left"
                      onClick={handleAddBucket}
                    >
                      Add New Bucket
                    </Button>
                  </div>

                  {error && (
                    <div className="bg-destructive/10 border border-destructive text-destructive rounded-lg p-4 mb-4">
                      <p className="font-medium">Error</p>
                      <p className="text-sm mt-1">{error}</p>
                    </div>
                  )}

                  {loading ? (
                    <div className="flex items-center justify-center py-12">
                      <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
                    </div>
                  ) : buckets?.length === 0 ? (
                    <div className="bg-card border border-border rounded-lg p-12 text-center">
                      <Icon name="Database" size={48} className="mx-auto mb-4 text-muted-foreground" />
                      <h3 className="text-lg font-semibold text-foreground mb-2">No Buckets Configured</h3>
                      <p className="text-sm text-muted-foreground mb-6">
                        Add your first S3 bucket to start managing files
                      </p>
                      <Button
                        iconName="Plus"
                        iconPosition="left"
                        onClick={handleAddBucket}
                      >
                        Add Your First Bucket
                      </Button>
                    </div>
                  ) : (
                    <div className="grid gap-6 md:grid-cols-2">
                      {buckets?.map((bucket) => (
                        <BucketConfigCard
                          key={bucket?.id}
                          bucket={bucket}
                          onEdit={handleEditBucket}
                          onRemove={handleRemoveBucket}
                          onTestConnection={handleTestConnection}
                        />
                      ))}
                    </div>
                  )}
                </div>
              )}

              {activeTab === 'storage' && (
                <div>
                  <div className="mb-6">
                    <h2 className="text-xl font-semibold text-foreground mb-1">Storage Management</h2>
                    <p className="text-sm text-muted-foreground">
                      Request and manage user-specific storage folders
                    </p>
                  </div>
                  <div className="bg-card border border-border rounded-lg p-6">
                    <StorageRequest />
                  </div>
                </div>
              )}

              {activeTab === 'preferences' && (
                <div>
                  <div className="mb-6">
                    <h2 className="text-xl font-semibold text-foreground mb-1">Application Preferences</h2>
                    <p className="text-sm text-muted-foreground">
                      Customize your file management experience
                    </p>
                  </div>
                  <div className="bg-card border border-border rounded-lg p-6">
                    <AppPreferences
                      preferences={preferences}
                      onPreferenceChange={handlePreferenceChange}
                    />
                  </div>
                </div>
              )}

              {activeTab === 'session' && (
                <div>
                  <div className="mb-6">
                    <h2 className="text-xl font-semibold text-foreground mb-1">Session Management</h2>
                    <p className="text-sm text-muted-foreground">
                      Manage your authentication and security settings
                    </p>
                  </div>
                  <div className="bg-card border border-border rounded-lg p-6">
                    <SessionManagement
                      session={session}
                      onRefreshToken={handleRefreshToken}
                      onSignOut={handleSignOut}
                    />
                  </div>
                </div>
              )}

              {activeTab === 'pwa' && (
                <div>
                  <div className="mb-6">
                    <h2 className="text-xl font-semibold text-foreground mb-1">PWA Status</h2>
                    <p className="text-sm text-muted-foreground">
                      Progressive Web App installation and offline capabilities
                    </p>
                  </div>
                  <div className="bg-card border border-border rounded-lg p-6">
                    <PWAStatus />
                  </div>
                </div>
              )}

              {activeTab === 'export' && (
                <div>
                  <div className="mb-6">
                    <h2 className="text-xl font-semibold text-foreground mb-1">Configuration Management</h2>
                    <p className="text-sm text-muted-foreground">
                      Export and import your bucket configurations
                    </p>
                  </div>
                  <div className="bg-card border border-border rounded-lg p-6">
                    <ConfigExportImport
                      buckets={buckets}
                      onImport={handleImportConfig}
                    />
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </main>
      <BucketConfigModal
        isOpen={isConfigModalOpen}
        onClose={() => setIsConfigModalOpen(false)}
        onSave={handleSaveBucket}
        editBucket={editingBucket}
      />
    </div>
  );
};

export default Settings;